import { createApp } from "vue";
import { createPinia } from "pinia";
import { VueQueryPlugin, QueryClient } from "@tanstack/vue-query";
import { MotionPlugin } from "@vueuse/motion";
import App from "./App.vue";
import router from "./router";
import { bootBranding } from "@/lib/branding";
import { applyCachedSkinSync, bootSkin } from "@/lib/theme";
import { NATIVO } from "@/lib/plataforma";
import { marcarBoot, marcasDoBoot } from "@/lib/boot";
import "./styles/app.css";

// Pele (tema) aplicada ANTES de qualquer coisa pintar: usa o último valor
// conhecido (localStorage) ou o preview de sessão, sem tocar na rede.
applyCachedSkinSync();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, err: any) => {
        if (err?.status === 401 || err?.status === 403) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: { retry: 0 },
  },
});

marcarBoot("modulos carregados");
const pinia = createPinia();
const app = createApp(App);

app.use(pinia);

// Branding roda DEPOIS do Pinia (usa store) e ANTES do mount, mas NUNCA pode
// segurar o mount: ele consulta o Supabase, e sem rede (leiturista em campo,
// subsolo, celular sem sinal) o app ficava em tela branca para sempre.
marcarBoot("branding: inicio");
// Pele global resolve em paralelo com o branding e NUNCA segura o mount: quando
// a resposta do app_config chega, o data-theme é reaplicado (troca imperceptível
// se já vinha do cache).
bootSkin().catch((err) => console.error("Skin boot failed:", err));
await Promise.race([
  bootBranding().catch((err) => console.error("Branding boot failed:", err)),
  new Promise((r) => setTimeout(r, 2500)),
]);

marcarBoot("branding: fim");
app.use(router);
app.use(VueQueryPlugin, { queryClient });
app.use(MotionPlugin);

/**
 * Rede de segurança do arranque.
 *
 * Se algo estourar antes da primeira tela, o resultado era um retângulo branco
 * sem nenhuma pista, e dentro do APK não há console para consultar. Aqui o erro
 * vira texto na tela, que o leiturista pode fotografar.
 */
function telaDeFalha(motivo: string) {
  const el = document.getElementById("app");
  if (!el || el.childElementCount > 0) return; // já montou, não atrapalha
  el.innerHTML = `
    <div style="font:15px/1.5 system-ui,sans-serif;color:#0E143A;box-sizing:border-box;
                padding:calc(env(safe-area-inset-top,0px) + 20px) 18px calc(env(safe-area-inset-bottom,0px) + 24px);
                max-width:560px;margin:0 auto">
      <div style="font-size:19px;font-weight:800;margin:0 0 6px">O aplicativo não abriu</div>
      <p style="color:#4A5578;margin:0 0 14px">Feche e abra de novo. Se continuar, mostre esta tela para o suporte.</p>
      <pre style="white-space:pre-wrap;overflow-wrap:anywhere;background:#F2F5FB;border-radius:12px;
                  padding:12px;font-size:12px;margin:0 0 12px;box-sizing:border-box">${motivo}</pre>
      <div style="font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#8A93AD;margin:0 0 6px">
        Até onde chegou
      </div>
      <pre style="white-space:pre-wrap;overflow-wrap:anywhere;background:#F2F5FB;border-radius:12px;
                  padding:12px;font-size:11px;margin:0;box-sizing:border-box">${marcasDoBoot()}</pre>
    </div>`;
}

/**
 * Erros de componente NÃO chegam no window.onerror: o Vue os captura e só
 * imprime no console, que não existe dentro do APK. Sem isto, uma exceção no
 * setup de uma tela vira retângulo branco sem pista nenhuma.
 */
app.config.errorHandler = (err: any, _inst, info) => {
  console.error("Vue:", err, info);
  telaDeFalha(`Erro na tela (${info})

${err?.stack || err?.message || String(err)}`);
};

/** Falha ao carregar o pedaço de código de uma rota: o router aborta calado. */
router.onError((err: any) => {
  console.error("Router:", err);
  telaDeFalha(`Falha ao abrir a tela

${err?.stack || err?.message || String(err)}`);
});

router.afterEach((to) => marcarBoot("renderizou:" + to.path));

marcarBoot("mount: inicio");
try {
  app.mount("#app");
  marcarBoot("mount: fim");
} catch (e: any) {
  telaDeFalha(e?.stack || e?.message || String(e));
  throw e;
}

window.addEventListener("error", (e) => telaDeFalha(e.message || "erro desconhecido"));
window.addEventListener("unhandledrejection", (e: any) =>
  telaDeFalha(e?.reason?.stack || e?.reason?.message || "promessa rejeitada"),
);
// se em 12s nada renderizou, algo travou antes da primeira tela
setTimeout(() => telaDeFalha("A tela inicial não carregou em 12 segundos."), 12_000);

// Auto-update GLOBAL do PWA (admin + campo). Registra o SW em TODA página, checa
// atualização na hora e ao focar/90s, força o SW novo a assumir (skipWaiting) e recarrega
// 1× quando ele assume, para o usuário nunca ficar preso num bundle velho em cache.
if (NATIVO && "serviceWorker" in navigator) {
  // Dentro do APK o service worker só atrapalha: os arquivos já estão no
  // aparelho. Pior, o sw.js empacotado é a lápide de limpeza; registrá-lo aqui
  // criava laço infinito (ativa, desregistra, navega, o main registra de novo)
  // e o app abria em tela branca. Aqui só limpamos o que tenha sobrado.
  navigator.serviceWorker.getRegistrations()
    .then((rs) => rs.forEach((r) => r.unregister()))
    .catch(() => {});
  caches?.keys?.().then((ks) => ks.forEach((k) => caches.delete(k))).catch(() => {});
} else if ("serviceWorker" in navigator) {
  let reloaded = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloaded) return;
    reloaded = true;
    location.reload();
  });
  navigator.serviceWorker.register("/sw.js").then((reg) => {
    const ativar = () => reg.waiting?.postMessage?.({ type: "SKIP_WAITING" });
    reg.update().catch(() => {});
    if (reg.waiting) ativar(); // já há um SW novo esperando: assume agora
    reg.addEventListener("updatefound", () => {
      const sw = reg.installing;
      sw?.addEventListener("statechange", () => { if (sw.state === "installed" && navigator.serviceWorker.controller) ativar(); });
    });
    const check = () => reg.update().catch(() => {});
    setInterval(check, 90_000);
    window.addEventListener("focus", check);
  }).catch(() => {});
}
